import { useCallback, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { Play, Square, Loader2, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import cloudAgentService, { CloudJob, CloudView } from '@/services/cloudAgent.service';
import { useTranslation } from 'react-i18next';
import StrobiAvatar from '@/components/StrobiAvatar';

const isRunning = (j: CloudJob | null) => j?.status === 'running' || j?.status === 'stopping';

/**
 * Sidebar Cloud-Agent Umschalter + Live-Status.
 * - /agent/* (X-Agent-Key) ile başlat/durdur/durum.
 * - /agent-socket 'agent:progress' ile CANLI: telefondan durdurulunca web yenilemeden "Aus"a düşer.
 * - Ek güvenlik: çalışırken 5sn poll (socket kaçarsa da senkron kalır).
 */
export default function CloudAgentToggle({ collapsed = false }: { collapsed?: boolean }) {
  const { t } = useTranslation();
  const [job, setJob] = useState<CloudJob | null>(null);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [views, setViews] = useState<CloudView[]>([]);
  const [viewsLoaded, setViewsLoaded] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const running = isRunning(job);

  const refresh = useCallback(async () => {
    try {
      const j = await cloudAgentService.getStatus();
      setJob(j);
    } catch {
      /* sessiz */
    }
  }, []);

  // İlk durum + socket
  useEffect(() => {
    refresh();
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    const socket = io(window.location.origin, {
      path: '/agent-socket',
      auth: { token, role: 'web' },
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;
    socket.on('agent:progress', (p: any) => {
      if (p?.done || p?.status === 'stopped') {
        setJob((prev) => (prev ? { ...prev, status: 'stopped' } : prev));
        return;
      }
      // Canlı istatistik güncelle (idle dahil çalışıyor)
      setJob((prev) => ({
        ...(prev || ({ id: p.jobId, status: 'running' } as CloudJob)),
        status: 'running',
        processed: p.processed ?? prev?.processed,
        drafted: p.drafted ?? prev?.drafted,
        noted: p.noted ?? prev?.noted,
        skipped: p.skipped ?? prev?.skipped,
        current_ticket_number: p.idle ? null : (p.ticketNumber ?? prev?.current_ticket_number),
      }));
    });
    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [refresh]);

  // Çalışırken 5sn poll (socket kaçarsa güvenlik ağı + istatistik)
  useEffect(() => {
    if (!running) return;
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [running, refresh]);

  const doStart = async (viewId?: string | null) => {
    setPickerOpen(false);
    setBusy(true);
    setJob((prev) => ({ ...(prev || ({} as CloudJob)), id: prev?.id || 'pending', status: 'running' }));
    try {
      await cloudAgentService.start(viewId);
      toast.success(t('common:x2.cloudAgentStarted', 'Cloud-Agent gestartet'));
      setTimeout(refresh, 1200);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Start fehlgeschlagen');
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const doStop = async () => {
    setBusy(true);
    setJob((prev) => (prev ? { ...prev, status: 'stopping' } : prev));
    try {
      await cloudAgentService.stop();
      toast.success(t('common:x2.cloudAgentStopping', 'Cloud-Agent wird gestoppt'));
      setTimeout(refresh, 1200);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Stopp fehlgeschlagen');
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const openPicker = async () => {
    setPickerOpen((o) => !o);
    if (!viewsLoaded) {
      try {
        const v = await cloudAgentService.getViews();
        setViews(v);
      } catch {
        /* sessiz */
      } finally {
        setViewsLoaded(true);
      }
    }
  };

  const onToggle = () => {
    if (busy) return;
    if (running) doStop();
    else openPicker();
  };

  const statusLine = (() => {
    if (job?.status === 'stopping') return 'Stoppt…';
    if (!running) return '';
    const parts: string[] = [];
    if (job?.view_name) parts.push(job.view_name);
    else parts.push('Alle Tickets');
    if (job?.current_ticket_number) parts.push(`#${job.current_ticket_number}`);
    else if ((job?.drafted ?? 0) > 0 || (job?.processed ?? 0) > 0) parts.push(`${job?.drafted ?? 0} Entwürfe`);
    return parts.join(' · ');
  })();

  // Collapsed (dar) mod: tek ikon-buton, dot renkli
  if (collapsed) {
    return (
      <button
        onClick={onToggle}
        disabled={busy}
        title={running ? 'Cloud-Agent läuft — stoppen' : 'Cloud-Agent starten'}
        className="flex h-7 w-7 items-center justify-center rounded hover:bg-white/10 disabled:opacity-50"
      >
        <StrobiAvatar
          state="excited"
          tone="sidebar"
          size={24}
          className={busy ? 'opacity-60' : undefined}
        />
      </button>
    );
  }

  return (
    <div className="relative mb-2">
      {/* View seçici popover */}
      {pickerOpen && !running && (
        <div className="absolute bottom-full left-0 z-50 mb-1 w-full overflow-hidden rounded-md border border-white/10 bg-[#0b1220] py-1 shadow-xl">
          <div className="px-3 py-1 text-[9px] font-semibold uppercase tracking-wide text-white/40">{t('common:x2.queue', 'Warteschlange')}</div>
          <button
            onClick={() => doStart(null)}
            className="flex w-full items-center justify-between px-3 py-1.5 text-left text-[11px] text-white/80 hover:bg-white/10"
          >
            {t('common:x2.allOpenTickets', 'Alle offenen Tickets')} <ChevronRight className="h-3 w-3 opacity-40" />
          </button>
          {views.map((v) => (
            <button
              key={v.id}
              onClick={() => doStart(v.id)}
              className="flex w-full items-center justify-between px-3 py-1.5 text-left text-[11px] text-white/80 hover:bg-white/10"
            >
              <span className="truncate">{v.name}</span>
              <ChevronRight className="h-3 w-3 shrink-0 opacity-40" />
            </button>
          ))}
          {viewsLoaded && views.length === 0 && (
            <div className="px-3 py-1.5 text-[10px] text-white/30">{t('common:x2.noOwnViews', 'Keine eigenen Ansichten')}</div>
          )}
        </div>
      )}

      <div className="cloud-agent-sidebar-card flex items-center justify-between gap-2 rounded-md border border-white/[0.08] bg-white/5 px-2.5 py-1.5">
        <div className="flex min-w-0 items-center gap-2">
          <StrobiAvatar state="excited" tone="sidebar" size={23} className="shrink-0" />
          <div className="min-w-0">
            <div className="text-[11px] font-semibold text-white/90">{t('common:x1.cloudAgent', 'Cloud-Agent')}</div>
            {statusLine && <div className="truncate text-[10px] text-white/50">{statusLine}</div>}
          </div>
        </div>
        <button
          onClick={onToggle}
          disabled={busy}
          title={running ? 'Stoppen' : 'Starten'}
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded transition disabled:opacity-50 ${
            running
              ? 'text-rose-300 hover:bg-rose-500/20'
              : 'text-emerald-300 hover:bg-emerald-500/20'
          }`}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : running ? <Square className="h-3 w-3" /> : <Play className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );
}
